import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineController,
  LineElement, PointElement, BarController, BarElement, Tooltip,
} from 'chart.js';
import { setPaneMode, setPaneUnit } from '../../slices/performancePaneSlice';
import { getChartTooltipStyle } from '../../utils/chartDefaults';
import SegmentedControl from '../SegmentedControl';
import { marketColor, ENSEMBLE_COLOR } from './colors';
import {
  mergedUserSeries, ensembleRankSeries, bestErrorSeries, bucketLabelUnion, humanizeResourceName,
} from './paneData';

ChartJS.register(
  CategoryScale, LinearScale, LineController, LineElement, PointElement,
  BarController, BarElement, Tooltip,
);

// Cursor-following tooltip positioner (idempotent module-scope assignment):
// keeps the tooltip beside the pointer instead of parking it over the traces
// at the averaged position of the hovered day's points.
Tooltip.positioners.paneCursor = (items, eventPosition) =>
  (eventPosition ? { x: eventPosition.x, y: eventPosition.y } : false);

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

const MODES = [{ value: 'rank', label: 'Rank' }, { value: 'score', label: 'Score' }];
const UNITS = [{ value: 'mw', label: 'MW' }, { value: 'best', label: 'vs best' }];
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const metricLabel = (track) => (track === 'probabilistic' ? 'MWI' : 'RMSE');

// Even integer tick step for a rank axis of the given depth: aim for ~8
// gridlines rounded to a 1/2/5/10 step so, anchored at #1, every tick sits one
// step from the next (no crammed #1/#2 pair against evenly spaced rest).
const rankTickStep = (maxRank) => {
  const raw = Math.max(1, Math.ceil(maxRank / 8));
  return [1, 2, 5, 10, 20, 50].find((s) => s >= raw) || Math.ceil(raw / 10) * 10;
};

// Rank/score evolution for the selected forecaster across the markets they
// appear in this month. Mode (Rank | Score) and unit (MW | vs best) are
// pane-level state shared with StandingsCard; trace visibility and the
// ensemble overlay are local UI state, not persisted. Rank is a line (an
// ordinal trajectory); score is bars (a per-day magnitude); "vs best" pairs
// the forecaster's daily error against the day's best forecaster.
const EvolutionChartCard = ({ pane, userId, visibleResources }) => {
  const dispatch = useDispatch();
  const [hidden, setHidden] = useState(() => new Set());
  const [showEnsemble, setShowEnsemble] = useState(false);
  const dark = isDark();
  const textColor = dark ? '#e5e7eb' : '#374151';
  const gridColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
  // Neutral reference tone for the "best forecaster" bar, so the market-colored
  // "you" bar stays the figure and the benchmark reads as ground.
  const refColor = dark ? '#64748b' : '#94a3b8';
  const monthAbbr = MONTHS_ABBR[((pane.month || 1) - 1) % 12];

  const active = visibleResources.filter((x) => !hidden.has(x.resource.id));

  // Sorted union of scored days across ALL visible markets (not just the
  // active/unhidden ones), so every strip shares one chronological x axis
  // regardless of which market has data for which day, and regardless of
  // trace toggling.
  const labels = useMemo(
    () => bucketLabelUnion(visibleResources.map((x) => x.data.series)),
    [visibleResources]
  );

  // Backend never sends ensemble rows to forecaster-role callers; only show
  // the toggle when at least one visible market actually has ensemble data.
  const hasEnsembleData = useMemo(
    () => visibleResources.some((x) => ensembleRankSeries(x.data.series).length > 0),
    [visibleResources]
  );

  const toggle = (resourceId) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId); else next.add(resourceId);
      return next;
    });
  };

  // Daily observations are sparse (at most one point per day of the month), so
  // every line point wears a resting marker; the surface-colored ring keeps
  // markers readable where traces cross.
  const surface = dark ? '#151d2c' : '#fcfcfc';
  const lineDataset = (x, points) => ({
    label: humanizeResourceName(x.resource.name),
    data: points,
    borderColor: marketColor(x.index, dark),
    backgroundColor: marketColor(x.index, dark),
    borderWidth: 2,
    // Unclip so markers sitting on the scale edge (rank #1) draw into the
    // chart's top padding instead of being cropped.
    clip: false,
    pointRadius: 3,
    pointBackgroundColor: marketColor(x.index, dark),
    pointBorderColor: surface,
    pointBorderWidth: 2,
    pointHoverRadius: 5,
    pointHitRadius: 10,
    tension: 0,
    spanGaps: true,
  });

  // categoryPercentage sets how much of a day's slot the bar group fills (day
  // separation); barPercentage sets the gap between bars inside the group. For
  // the You/Best pair we keep the group narrow but let the two bars nearly
  // touch, so a day reads as one pair rather than two scattered bars.
  const barDataset = (x, points, opts = {}) => ({
    label: humanizeResourceName(x.resource.name),
    data: points,
    backgroundColor: opts.color || marketColor(x.index, dark),
    borderWidth: 0,
    borderRadius: 3,
    borderSkipped: false,
    categoryPercentage: opts.categoryPercentage ?? 0.5,
    barPercentage: opts.barPercentage ?? 0.9,
    ...(opts.maxBarThickness ? { maxBarThickness: opts.maxBarThickness } : {}),
  });

  // Dashed vertical crosshair through the hovered day, matching the main
  // dashboard chart's hover treatment (line strips only).
  const crosshairPlugin = useMemo(() => ({
    id: 'paneCrosshair',
    afterDatasetsDraw(chart) {
      const els = chart.tooltip?.getActiveElements?.() || [];
      if (!els.length) return;
      const { ctx, chartArea } = chart;
      const { x } = els[0].element;
      ctx.save();
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.6)';
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  }), []);

  const metric = metricLabel(pane.track);

  const baseOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8, right: 12 } },
    // Index mode: hovering any day lists every series' value for that day.
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...getChartTooltipStyle(dark),
        position: 'paneCursor',
        caretSize: 0,
        caretPadding: 14,
        // Best value first (lower is better in every mode of this chart).
        itemSort: (a, b) => a.parsed.y - b.parsed.y,
        callbacks: {
          title: (items) => (items.length ? `${monthAbbr} ${items[0].label}` : ''),
          // Renders rank + score from the point's own data (ctx.raw) in a
          // mode-specific order (ctx.dataset.tooltipOrder), omitting whichever
          // piece is absent.
          label: (ctx) => {
            const raw = ctx.raw || {};
            const order = ctx.dataset.tooltipOrder || ['rank', 'score'];
            const parts = {
              rank: raw.rank != null ? `#${raw.rank}` : null,
              score: raw.score != null ? `${metric} ${raw.score.toFixed(1)}` : null,
            };
            const line = order.map((k) => parts[k]).filter(Boolean).join(' · ');
            return line ? `${ctx.dataset.label}: ${line}` : ctx.dataset.label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          maxTicksLimit: 10,
          maxRotation: 0,
          callback(value) {
            return `${monthAbbr} ${this.getLabelForValue(value)}`;
          },
        },
        grid: { display: false },
        border: { color: gridColor },
      },
      y: {
        ticks: { color: textColor },
        grid: { color: gridColor },
        border: { display: false },
      },
    },
  }), [dark, textColor, gridColor, metric, monthAbbr]);

  // Per-market strips share the growing vertical space equally. Each keeps its
  // own y scale so unequal field sizes (e.g. #7-of-30 vs #17-of-23) and unequal
  // error magnitudes are never flattened onto one misleading shared axis.
  const scoreBarOptions = useMemo(() => ({
    ...baseOptions,
    scales: {
      ...baseOptions.scales,
      y: { ...baseOptions.scales.y, beginAtZero: true },
    },
  }), [baseOptions]);

  const userPoints = (x, key) =>
    mergedUserSeries(x.data.series, userId)
      .filter((d) => d[key] != null)
      .map((d) => ({ ...d, y: d[key] }));
  const latestOf = (pts) => (pts.length ? pts[pts.length - 1] : null);

  // Rank axis per strip: anchor #1 at the top and end on an aligned tick so the
  // gridlines are evenly spaced for this market's rank depth.
  const rankOptionsFor = (x) => {
    const ranks = userPoints(x, 'rank').map((p) => p.rank);
    if (showEnsemble) ensembleRankSeries(x.data.series).forEach((d) => ranks.push(d.rank));
    const maxRank = ranks.length ? Math.max(...ranks) : 1;
    const step = rankTickStep(maxRank);
    const max = 1 + step * Math.ceil((maxRank - 1) / step);
    return {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          reverse: true,
          min: 1,
          max,
          ticks: { ...baseOptions.scales.y.ticks, stepSize: step, precision: 0, callback: (v) => `#${v}` },
        },
      },
    };
  };

  const rankDatasets = (x) => {
    const ds = [{ ...lineDataset(x, userPoints(x, 'rank')), tooltipOrder: ['rank', 'score'] }];
    if (showEnsemble) {
      const ens = ensembleRankSeries(x.data.series).map((d) => ({ x: d.label, y: d.rank, rank: d.rank }));
      if (ens.length) {
        ds.push({
          ...lineDataset(x, ens),
          label: 'Ensemble',
          borderColor: ENSEMBLE_COLOR,
          backgroundColor: ENSEMBLE_COLOR,
          borderDash: [5, 4],
          tooltipOrder: ['rank'],
        });
      }
    }
    return ds;
  };
  const mwDatasets = (x) => [{
    ...barDataset(x, userPoints(x, 'score'), { categoryPercentage: 0.45, barPercentage: 0.6, maxBarThickness: 40 }),
    tooltipOrder: ['score', 'rank'],
  }];
  const bestDatasets = (x) => {
    const you = userPoints(x, 'score').map((d) => ({ x: d.x, y: d.score, score: d.score }));
    const best = bestErrorSeries(x.data.series).map((d) => ({ x: d.label, y: d.best, score: d.best }));
    const spacing = { categoryPercentage: 0.5, barPercentage: 0.94 };
    return [
      { ...barDataset(x, you, spacing), label: 'You', tooltipOrder: ['score'] },
      { ...barDataset(x, best, { ...spacing, color: refColor }), label: 'Best', tooltipOrder: ['score'] },
    ];
  };

  const rankHeaderRight = (x) => {
    const latest = latestOf(userPoints(x, 'rank'));
    return latest ? (
      <span className="font-mono text-xs tabular-nums text-gray-500 dark:text-gray-400">#{latest.rank}</span>
    ) : null;
  };
  const mwHeaderRight = (x) => {
    const latest = latestOf(userPoints(x, 'score'));
    return latest ? (
      <span className="font-mono text-xs tabular-nums text-gray-500 dark:text-gray-400">{latest.score.toFixed(1)}</span>
    ) : null;
  };
  const bestHeaderRight = (x) => (
    <span className="inline-flex flex-shrink-0 items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
      <span className="inline-flex items-center gap-1">
        <i className="h-2 w-2 rounded-sm" style={{ background: marketColor(x.index, dark) }} />You
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="h-2 w-2 rounded-sm" style={{ background: refColor }} />Best
      </span>
    </span>
  );

  const renderStrips = ({ buildDatasets, options, ChartComp, headerRight, plugins }) =>
    active.map((x) => (
      <div key={x.resource.id} className="view-enter flex h-56 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <span className="inline-flex min-w-0 items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
            <i className="h-2 w-2 flex-shrink-0 rounded-sm" style={{ background: marketColor(x.index, dark) }} />
            <span className="truncate">{humanizeResourceName(x.resource.name)}</span>
          </span>
          {headerRight(x)}
        </div>
        {/* Chart.js with maintainAspectRatio:false must measure a parent it
            does not size, or the first canvas balloons and starves its
            siblings; the absolute layer takes the flex-distributed height. */}
        <div className="relative mt-1 min-h-0 flex-1">
          <div className="absolute inset-0">
            <ChartComp data={{ labels, datasets: buildDatasets(x) }} options={typeof options === 'function' ? options(x) : options} plugins={plugins} />
          </div>
        </div>
      </div>
    ));

  let body;
  if (pane.mode === 'rank') {
    body = renderStrips({ buildDatasets: rankDatasets, options: rankOptionsFor, ChartComp: Line, headerRight: rankHeaderRight, plugins: [crosshairPlugin] });
  } else if (pane.unit === 'best') {
    body = renderStrips({ buildDatasets: bestDatasets, options: scoreBarOptions, ChartComp: Bar, headerRight: bestHeaderRight, plugins: [] });
  } else {
    body = renderStrips({ buildDatasets: mwDatasets, options: scoreBarOptions, ChartComp: Bar, headerRight: mwHeaderRight, plugins: [] });
  }

  return (
    <section aria-label="Evolution" className="flex flex-col">
      <div className="flex items-baseline justify-between">
        <h5 className="text-[11px] font-mono uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 font-normal">
          Evolution
        </h5>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          {pane.mode === 'rank' ? 'Daily rank · best on top'
            : pane.unit === 'best' ? `Daily ${metric} vs the day's best · lower is better`
              : `Daily ${metric} · lower is better`}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <SegmentedControl
          options={MODES}
          value={pane.mode}
          onChange={(m) => dispatch(setPaneMode(m))}
          ariaLabel="Chart mode"
          size="sm"
        />
        {pane.mode === 'score' && (
          <SegmentedControl
            options={UNITS}
            value={pane.unit === 'best' ? 'best' : 'mw'}
            onChange={(u) => dispatch(setPaneUnit(u))}
            ariaLabel="Score view"
            size="sm"
          />
        )}
      </div>
      <div className="mt-4 flex flex-col gap-4">{body}</div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {visibleResources.map((x) => (
          <button
            key={x.resource.id}
            type="button"
            onClick={() => toggle(x.resource.id)}
            aria-pressed={!hidden.has(x.resource.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border border-gray-200/70 dark:border-gray-600/70 px-2.5 py-1 text-[11px] text-gray-700 dark:text-gray-200 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ${hidden.has(x.resource.id) ? 'opacity-40' : ''}`}
          >
            <i className="h-2 w-2 rounded-sm" style={{ background: marketColor(x.index, dark) }} />
            {humanizeResourceName(x.resource.name)}
          </button>
        ))}
        {pane.mode === 'rank' && hasEnsembleData && (
          <button
            type="button"
            onClick={() => setShowEnsemble((v) => !v)}
            aria-pressed={showEnsemble}
            className={`inline-flex items-center gap-1.5 rounded-full border border-gray-200/70 dark:border-gray-600/70 px-2.5 py-1 text-[11px] text-gray-700 dark:text-gray-200 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ${showEnsemble ? '' : 'opacity-40'}`}
          >
            <i className="h-2 w-2 rounded-sm" style={{ background: ENSEMBLE_COLOR }} />
            Ensemble
          </button>
        )}
      </div>
    </section>
  );
};

export default EvolutionChartCard;
